import './observer-tag.scss';

import { type ReactNode, useEffect, useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import type { LocationTrackingState } from '../../../hooks/useLocationTracking';
import { useObserverData } from '../../../hooks/useObserverData';
import { distanceMetres, formatDistance } from '../../../hooks/utils/distance';
import type { IObserverCredential, IObserverPosting, ObserverDataStatus } from '../../../interface/observer';
import { TypeButton } from '../../utils/button';
import { BeaconIcon } from '../reports-hub/icons';
import { deriveNextStep, directionsUrl, type ObserverNextStep, trustedDistance } from './observer-next-step';

/** "PollingUnit" → "Polling unit"; the API's level names are identifiers, not copy. */
const LEVEL_COPY: Record<string, string> = {
	PollingUnit: 'Polling unit',
	Ward: 'Ward',
	LGA: 'LGA',
	State: 'State',
};

/**
 * INEC stores place and election names in capitals ("ODO - EMURE I"). Shouted names are slow to read
 * at a glance, so all-caps values are set in title case for display; Roman numerals and short
 * acronyms (LGA, PU, FCT) keep their capitals. Mixed-case values are left as entered.
 */
export const displayName = (value: string | null | undefined): string => {
	if (!value) return '';
	if (value !== value.toUpperCase() || !/[A-Z]/.test(value)) return value;
	return value
		.toLowerCase()
		.replace(/[a-z0-9']+/g, (word) =>
			/^(x{0,3})(ix|iv|v?i{0,3})$/.test(word) || ['lga', 'pu', 'fct'].includes(word)
				? word.toUpperCase()
				: word[0].toUpperCase() + word.slice(1)
		);
};

const placeName = (p: IObserverPosting) =>
	displayName(p.pollingUnitName || p.wardName || p.lgaName || p.stateName) || p.entityCode;

/** The places above the posting, outermost last, so the line reads like an address. */
const placeTrail = (p: IObserverPosting) =>
	[p.pollingUnitName ? p.wardName : null, p.wardName || p.pollingUnitName ? p.lgaName : null, p.stateName]
		.filter(Boolean)
		.map(displayName)
		.join(', ');

const initials = (name: string) =>
	name
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map((w) => w[0]?.toUpperCase())
		.join('');

const sinceText = (iso: string | null) => {
	if (!iso) return null;
	const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
	if (minutes < 1) return 'updated just now';
	if (minutes < 60) return `updated ${minutes} min ago`;
	return `updated ${Math.floor(minutes / 60)} h ago`;
};

/** Re-render every 30 s so "updated 2 min ago" does not freeze between ten-minute pings. */
const useMinuteTick = (enabled: boolean) => {
	const [, setTick] = useState(0);
	useEffect(() => {
		if (!enabled) return undefined;
		const id = setInterval(() => setTick((t) => t + 1), 30_000);
		return () => clearInterval(id);
	}, [enabled]);
};

/**
 * The live line at the foot of the posting stub: where the observer is relative to their unit, and
 * whether their desk is receiving it. Every state names what is true now, never a spinner on its own.
 */
const PassLocationLine = ({ tracking, posting }: { tracking: LocationTrackingState; posting?: IObserverPosting }) => {
	const hasFix = tracking.latitude !== null && tracking.longitude !== null;
	useMinuteTick(hasFix);

	if (tracking.status === 'idle') return null;

	const distance = trustedDistance(
		hasFix && posting
			? distanceMetres(tracking, { latitude: posting.pollingUnitLatitude, longitude: posting.pollingUnitLongitude })
			: null,
		tracking.accuracy
	);
	const unit = displayName(posting?.pollingUnitName);

	let tone: 'live' | 'quiet' | 'warn' = 'live';
	let main: string;
	let meta: string | null = null;

	switch (tracking.status) {
		case 'needs-permission':
			tone = 'warn';
			main = 'Allow location when your browser asks';
			break;
		case 'permission-denied':
			tone = 'warn';
			main = 'Location is off';
			break;
		case 'initializing':
			tone = 'quiet';
			main = 'Finding your location…';
			break;
		case 'error':
			tone = 'warn';
			main = hasFix ? 'Location shared earlier' : 'No GPS fix yet';
			meta = hasFix ? 'could not refresh it' : 'move into the open and try again';
			break;
		default:
			main =
				distance === null
					? 'Location shared with your desk'
					: distance === 0
						? `At ${unit || 'your unit'}`
						: `${formatDistance(distance)} from ${unit || 'your unit'}`;
			meta = [
				tracking.accuracy !== null ? `±${Math.round(tracking.accuracy)} m` : null,
				tracking.status === 'send-failed' ? 'last update not sent, retrying' : sinceText(tracking.lastUpdated),
			]
				.filter(Boolean)
				.join(', ');
			if (tracking.status === 'send-failed') tone = 'warn';
			if (tracking.status === 'paused') tone = 'quiet';
	}

	return (
		<p className={`observer-pass__where observer-pass__where--${tone}`}>
			<span
				className={`observer-pass__beacon${tracking.status === 'active' ? ' observer-pass__beacon--pulse' : ''}`}
				aria-hidden='true'
			/>
			<span className='observer-pass__where-text'>
				{/* Only the status is live: the "updated N min ago" meta re-renders every 30 s and would be
				    re-announced forever. */}
				<span
					className='observer-pass__where-main'
					aria-live='polite'
				>
					{main}
				</span>
				{meta ? <span className='observer-pass__where-meta'>{meta}</span> : null}
			</span>
		</p>
	);
};

/**
 * The accreditation an observer wears on election day, as the first thing on their home screen: a
 * lanyard pass with who accredited them and their number, and a tear-off stub with where they are
 * posted. Neutral by design — an observer's screen carries no party colour.
 *
 * <p>The stub always says the true state of the posting: loading, not loaded (with the retry, inside
 * the stub), not posted, or posted. It never says "not posted" because a request failed.</p>
 */
export const AccreditationTag = ({
	credential,
	posting,
	fallbackName,
	status = 'ready',
	onRetry,
	children,
}: {
	credential: IObserverCredential | null;
	posting: IObserverPosting | undefined;
	fallbackName: string;
	status?: ObserverDataStatus;
	onRetry?: () => void;
	/** Rendered at the foot of the stub — the home screen's live location line. */
	children?: ReactNode;
}) => {
	const name = credential?.fullName || fallbackName;
	const accredited = credential?.organizationIsAccredited;

	return (
		<article
			className='observer-pass'
			aria-label='Your observer accreditation'
		>
			<header className='observer-pass__band'>
				<span
					className='observer-pass__slot'
					aria-hidden='true'
				/>
				<p className='observer-pass__org'>{credential?.organizationName || 'Your observer organisation'}</p>
				<p className='observer-pass__standing'>
					{accredited === false ? 'Accreditation pending' : 'Accredited election observer'}
				</p>
			</header>

			<div className='observer-pass__holder'>
				{credential?.photoUrl ? (
					<img
						className='observer-pass__photo'
						src={credential.photoUrl}
						alt=''
					/>
				) : (
					<span
						className='observer-pass__photo observer-pass__photo--initials'
						aria-hidden='true'
					>
						{initials(name)}
					</span>
				)}
				<div className='observer-pass__who'>
					<p className='observer-pass__name'>{name}</p>
					{credential?.accreditationNumber ? (
						<p className='observer-pass__number'>
							<span className='observer-pass__number-label'>Accreditation no.</span>
							<span className='observer-pass__figures observer-pass__number-value'>{credential.accreditationNumber}</span>
						</p>
					) : null}
				</div>
			</div>

			<div
				className='observer-pass__perforation'
				aria-hidden='true'
			/>

			<section
				className='observer-pass__stub'
				aria-label='Your posting'
			>
				{status === 'loading' ? (
					<div
						className='observer-pass__shimmer'
						aria-busy='true'
						aria-label='Loading your posting'
					/>
				) : status === 'unavailable' ? (
					<div className='observer-pass__stub-note'>
						<p>Your posting did not load. Check your signal, then try again.</p>
						{onRetry ? (
							<button
								type='button'
								className='observer-home__link'
								onClick={onRetry}
							>
								Try again
							</button>
						) : null}
					</div>
				) : posting ? (
					<>
						<p className='observer-pass__level'>{LEVEL_COPY[posting.level || ''] || 'Your posting'}</p>
						<p className='observer-pass__place'>{placeName(posting)}</p>
						{placeTrail(posting) ? <p className='observer-pass__trail'>{placeTrail(posting)}</p> : null}
						<p className='observer-pass__code observer-pass__figures'>{posting.entityCode}</p>
						{posting.electionName ? <p className='observer-pass__election'>{displayName(posting.electionName)}</p> : null}
					</>
				) : (
					<p className='observer-pass__stub-note'>
						Not posted yet. Your observer desk posts you to a unit after approval, and it appears here.
					</p>
				)}
				{children}
			</section>
		</article>
	);
};

const NEXT_COPY: Record<ObserverNextStep['kind'], { title: string; detail: string }> = {
	'location-off': {
		title: 'Turn on location',
		detail: 'Your desk uses it to know you are on the ground and safe. It is shared every ten minutes while the app is open.',
	},
	'awaiting-posting': {
		title: 'Wait for your posting',
		detail: 'You can report incidents now. Your checklist opens once your desk posts you to a unit.',
	},
	'head-to-unit': {
		title: 'Head to your unit',
		detail: 'Your checklist starts with arrival, so fill it in once you are there.',
	},
	'checklist': {
		title: 'Fill in your election-day checklist',
		detail: 'Arrival, opening, accreditation, voting and the count, one step at a time.',
	},
};

/** The independent observer's home: their pass, the one thing to do now, then the incident button. */
const ObserverOverview = () => {
	const { rsProps, locationTracking } = useGlobalContext();
	const observer = useObserverData();
	const posting = observer.postings[0];
	const firstName = getUserData()?.user?.FullName?.split(' ')[0] || 'there';

	const openReports = () => rsProps?.callSection({ action: 'create', component: 'reports-hub', title: 'Reports', max: true });
	const openChecklist = () =>
		rsProps?.callSection({ action: 'view', component: 'observer-checklist', title: 'Election-day checklist' });
	const openDesk = () => rsProps?.callSection({ action: 'view', component: 'support-chat', title: 'Observer desk' });

	const distance = trustedDistance(
		locationTracking && posting
			? distanceMetres(locationTracking, {
					latitude: posting.pollingUnitLatitude,
					longitude: posting.pollingUnitLongitude,
				})
			: null,
		locationTracking?.accuracy
	);
	const next = deriveNextStep({
		dataStatus: observer.status,
		posting,
		trackingStatus: locationTracking?.status,
		distance,
	});

	const nextAction = (() => {
		switch (next?.kind) {
			case 'location-off':
				return (
					<button
						type='button'
						className='observer-next__go'
						onClick={locationTracking?.retry}
					>
						{next.blocked ? 'I turned it on, check again' : 'Share my location'}
					</button>
				);
			case 'awaiting-posting':
				return (
					<button
						type='button'
						className='observer-next__go'
						onClick={openDesk}
					>
						Message your observer desk
					</button>
				);
			case 'head-to-unit':
				return (
					<a
						className='observer-next__go'
						href={directionsUrl(next.latitude, next.longitude)}
						target='_blank'
						rel='noreferrer'
					>
						Get directions to {displayName(next.place)}
					</a>
				);
			case 'checklist':
				return (
					<button
						type='button'
						className='observer-next__go'
						onClick={openChecklist}
					>
						Open checklist
					</button>
				);
			default:
				return null;
		}
	})();

	// The checklist and desk stay one tap away even when the next step is something else.
	const showChecklistLink = !!posting && next?.kind !== 'checklist';
	const showDeskLink = next?.kind !== 'awaiting-posting';

	return (
		<div className='observer-home pb-4 mb-4'>
			<p className='observer-home__greeting'>Good to have you on the ground, {firstName}.</p>

			<AccreditationTag
				credential={observer.credential}
				posting={posting}
				fallbackName={getUserData()?.user?.FullName || ''}
				status={observer.status}
				onRetry={observer.retry}
			>
				{locationTracking ? (
					<PassLocationLine
						tracking={locationTracking}
						posting={posting}
					/>
				) : null}
			</AccreditationTag>

			{next ? (
				<section
					className={`observer-next observer-next--${next.kind}`}
					aria-labelledby='observer-next-title'
				>
					<p className='observer-next__label'>Next</p>
					<h2
						id='observer-next-title'
						className='observer-next__title'
					>
						{next.kind === 'head-to-unit' ? `Head to ${displayName(next.place)}` : NEXT_COPY[next.kind].title}
					</h2>
					<p className='observer-next__detail'>{NEXT_COPY[next.kind].detail}</p>
					{nextAction}
				</section>
			) : null}

			<div className='observer-home__actions'>
				<TypeButton
					title='Report an incident'
					buttonSize='small'
					buttonType='danger'
					className='w-100'
					onClick={openReports}
					icon={<BeaconIcon size={15} />}
				/>
				<div className='observer-home__links'>
					{showChecklistLink ? (
						<button
							type='button'
							className='observer-home__link'
							onClick={openChecklist}
						>
							Election-day checklist
						</button>
					) : null}
					{showDeskLink ? (
						<button
							type='button'
							className='observer-home__link'
							onClick={openDesk}
						>
							Message your observer desk
						</button>
					) : null}
				</div>
			</div>

			{observer.postings.length > 1 ? (
				<section className='observer-home__more'>
					<h3 className='observer-home__more-title'>Also posted to</h3>
					<ul>
						{observer.postings.slice(1).map((p) => (
							<li key={`${p.electionId}:${p.entityCode}`}>
								<span>{placeName(p)}</span>
								<span className='observer-pass__figures'>{p.entityCode}</span>
							</li>
						))}
					</ul>
				</section>
			) : null}
		</div>
	);
};

export default ObserverOverview;
